/**
 * Shared helpers for filesystem-face connectors that delegate to a host-side
 * fleet supervisor (rclone for sharepoint/webdav/googledrive, git for git).
 *
 * When `SKAILE_FLEET_MANAGED_MOUNTS` lists a connector's id, the host has
 * already prepared the underlying mount (rclone FUSE mountpoint, or a git
 * worktree bind) and exposed it at the driver's target dir. The driver
 * short-circuits its own connect path after verifying the bind is live.
 */

import { readdir, readFile, realpath, stat } from "node:fs/promises";

/** Kernel mount table consulted by {@link isMountpoint}. */
const MOUNTINFO_PATH = "/proc/self/mountinfo";

/**
 * Test-only redirect of the mount table at a fixture, so driver tests can
 * simulate a live bind without root.
 *
 * Gated on vitest deliberately: read unconditionally, a stray
 * `SKAILE_FLEET_MOUNTINFO_PATH` in a real deployment would point the check at
 * a missing file, drop it back to the existence-only fallback, and silently
 * reintroduce the exact failure this module exists to catch.
 */
function testMountinfoOverride(): string | undefined {
  return process.env.VITEST ? process.env.SKAILE_FLEET_MOUNTINFO_PATH : undefined;
}

/**
 * Return `true` when `connectorId` appears in the comma-separated
 * `SKAILE_FLEET_MANAGED_MOUNTS` env var, indicating the host fleet owns this
 * mount.
 */
export function isFleetManaged(connectorId: string): boolean {
  const raw = process.env.SKAILE_FLEET_MANAGED_MOUNTS ?? "";
  if (raw.length === 0) return false;
  return raw
    .split(",")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .includes(connectorId);
}

/**
 * Decode the octal escapes (`\040` space, `\011` tab, `\012` newline, `\134`
 * backslash) the kernel applies to path fields in mountinfo.
 */
function decodeMountinfoPath(field: string): string {
  return field.replace(/\\(\d{3})/g, (_, octal: string) =>
    String.fromCharCode(Number.parseInt(octal, 8)),
  );
}

/**
 * Is `dir` an actual mount point, per the kernel mount table?
 *
 * Returns `null` when the table cannot be read (non-Linux dev hosts) so the
 * caller degrades to the weaker existence check instead of failing a mount it
 * has no way to inspect.
 *
 * Deliberately not an `st_dev` comparison against the parent: git-fleet
 * worktrees are bind mounts from the same filesystem, so their device id
 * matches the parent's and the check would report a live bind as missing.
 */
async function isMountpoint(dir: string, mountinfoPath: string): Promise<boolean | null> {
  let table: string;
  try {
    table = await readFile(mountinfoPath, "utf8");
  } catch {
    return null;
  }
  const target = await realpath(dir);
  for (const line of table.split("\n")) {
    // Field 5 (1-indexed) is the mount point, relative to the process root.
    const mountPoint = line.split(" ")[4];
    if (mountPoint !== undefined && decodeMountinfoPath(mountPoint) === target) return true;
  }
  return false;
}

/**
 * Sanity-check the host bind mount before declaring success. The host
 * process should have the FUSE mount live before the container starts, so
 * we verify the directory exists, is readable, and is genuinely a mount
 * point. Polls briefly to absorb any startup race between container start
 * and the kernel publishing the mountpoint inside the container.
 *
 * `readdir` surfaces FUSE-side errors that `stat` cannot (e.g. transport
 * endpoint not connected if the host process died after bind).
 *
 * The mountpoint assertion is what catches a target mismatch: when the driver
 * and the host fleet disagree about where the bind lands, the driver's own
 * target dir exists and reads fine as an empty local directory, and without
 * this check the session silently runs against container disk instead of the
 * remote.
 */
export async function ensureFleetMounted(
  dir: string,
  log: { info: (msg: string, data?: object) => void },
  options?: { timeoutMs?: number; pollIntervalMs?: number; mountinfoPath?: string },
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 3_000;
  const pollIntervalMs = options?.pollIntervalMs ?? 100;
  const mountinfoPath = options?.mountinfoPath ?? testMountinfoOverride() ?? MOUNTINFO_PATH;
  const deadline = Date.now() + timeoutMs;
  let lastErr: unknown = null;
  while (Date.now() < deadline) {
    try {
      const st = await stat(dir);
      if (!st.isDirectory()) {
        throw new Error(`fleet mountpoint ${dir} is not a directory`);
      }
      await readdir(dir);
      if ((await isMountpoint(dir, mountinfoPath)) === false) {
        throw new Error(
          `fleet mountpoint ${dir} is a plain directory, not a mount — the host bind is absent or landed on a different path`,
        );
      }
      log.info("fleet bind mount verified", { dir });
      return;
    } catch (err) {
      lastErr = err;
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
  }
  throw new Error(
    `fleet mount ${dir} not ready after ${timeoutMs}ms — check SKAILE_FLEET_MANAGED_MOUNTS env var and host bind: ${
      lastErr instanceof Error ? lastErr.message : String(lastErr)
    }`,
  );
}

/**
 * Health verdict for a fleet mount target.
 *
 * Kept so existing callers can keep typing `ConnectorManager.reportFleetHealth`
 * while the method is a no-op in the package-side bind-consumer.
 */
export type FleetHealthVerdict = "healthy" | "degraded" | "error";
